import { PAYING_NOW_STATUS_LABELS } from "@repo/types";

import { palette } from "./theme";
import { COMPANY_SEGMENT_LABELS } from "@repo/types";
import { SUPPORT_REPORT_SOURCE_LABELS } from "../utils/supportReportSource";

/**
 * `palette` with its numeric shades typed.
 *
 * `palette` is typed as MUI's `PaletteOptions`, which does not carry this
 * codebase's numeric-shade augmentation (a pre-existing `packages/ui` typing
 * gap), so every `palette.green[700]` below is a `TS7053` under strict tsc —
 * ~300 of them in this one file. That is the platform's existing baseline and
 * is not fixed here; what this cast does is stop NEW maps adding to it.
 *
 * ⚠️ Declared at the TOP, not beside its first user. It was introduced by
 * VRG-138 immediately above `SHIPMENT_LABEL_STATUSES`, which is fine for a map
 * declared after it and useless for one declared before — `const` is not
 * hoisted for module-init reads, so a map higher in the file cannot reach it.
 * Use this in any map you add.
 */
const shadedPalette = palette as unknown as Record<string, Record<number, string>>;

/**
 * Status values that collapse into ONE filter option.
 *
 * The Status filter is otherwise derived 1:1 from CERTIFICATE_STATUSES, which
 * breaks once several statuses share a label — you'd get three identical
 * "Transfer" rows, each matching only a third of the transfers. This maps the
 * single visible option to every value it should match.
 *
 * `cancel_request` is included: cancelling is still something that happens to a
 * transfer in flight, and the certificate is still mid-transfer while the request
 * is outstanding. Splitting it out put a fourth colour in the Status column for a
 * distinction the reader of that column can't act on.
 */
export const CERT_TRANSFER_STATUSES = [
  'transfer_initiated',
  'transfer_waiting_confirmation',
  'waiting_accept',
  'cancel_request',
] as const;

/**
 * `glossaryId` on every entry: hovering the chip shows that term's definition, with no extra
 * `(i)` glyph on the row (see `StatusChip`).
 *
 * All NINE keys carry one, and the four transfer keys deliberately share
 * `certificate-status-transfer` — they already share a LABEL, so a reader who hovers three
 * chips reading "Transfer" and gets three different definitions would have been told the
 * distinction matters when the comment above says explicitly that it does not.
 *
 * This map is the only status map wired so far, and on purpose. "Lost", "Stolen" and the
 * transfer states are terms of art whose consequences a reader cannot infer from the word;
 * "In stock", "Archived" and "Invited" in the maps below are not, and §59 warns that an
 * affordance on everything is how people learn to ignore the one that mattered.
 */
export const CERTIFICATE_STATUSES = {
  created: {
    label: "Created",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
    glossaryId: "certificate-status-created",
  },
  inactive: {
    label: "Inactive",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
    glossaryId: "certificate-status-inactive",
  },
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
    glossaryId: "certificate-status-active",
  },
  // TODO: Tre, discsuss the various transfer states - i think we should consoldiate to transfer on cert status
  // and then have these (first 4)  on transfer tow (preovenance) rather than on cert (cert woudl jsut say transfer status)
  //
  // These two keys MUST match CertificateStatus (packages/types). They previously
  // read `transfer_out` / `transfer_in`, which appear nowhere in that enum — so no
  // certificate could ever carry them, while the two real transfer states had no
  // entry at all. Two consequences, only one of them visible: StatusChip logged
  // "Invalid 'status' prop: transfer_waiting_confirmation" for every affected row
  // and rendered no chip, AND the Status filter (CERT_STATUS_FILTER_OPTIONS is
  // derived from this object) offered two options that match nothing while
  // omitting the two that exist.
  // All three in-flight transfer stages read simply "Transfer". They are substates
  // of one thing, the distinction between them is internal, and the longest of the
  // old labels ("Awaiting confirmation") overflowed the chip in the Status column.
  // Duplicate labels are safe HERE because chips are looked up by status KEY; the
  // filter dedupes via CERT_STATUS_FILTER_GROUPS so "Transfer" appears once and
  // matches all three.
  transfer_initiated: {
    label: "Transfer",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
    glossaryId: "certificate-status-transfer",
  },
  transfer_waiting_confirmation: {
    label: "Transfer",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
    glossaryId: "certificate-status-transfer",
  },
  waiting_accept: {
    label: "Transfer",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
    glossaryId: "certificate-status-transfer",
  },
  cancel_request: {
    label: "Transfer",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
    glossaryId: "certificate-status-transfer",
  },
  lost: {
    label: "Lost",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
    glossaryId: "certificate-status-lost",
  },
  stolen: {
    label: "Stolen",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
    glossaryId: "certificate-status-stolen",
  },
};

export const PRODUCT_STOCK_STATUSES = {
  in_stock: {
    label: "In stock",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  not_in_stock: {
    label: "Not in stock",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

export const PRODUCT_STATUSES = {
  archived: {
    label: "Archived",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

export const COMPANY_STATUSES = {
  // VRG-306 — the pipeline stage, carried on the same axis as active/suspended/
  // archived because that is the axis the Companies filter already drives, and
  // because a demo IS a company at an earlier stage rather than a separate
  // entity. Hidden by default like archived, but REACHABLE — see includeDemo.
  demo: {
    label: "Demo",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  suspended: {
    label: "Suspended",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
  archived: {
    label: "Archived",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

/**
 * VRG-718 — the Accounts → Companies segment chips and row type badges. Labels come from
 * `COMPANY_SEGMENT_LABELS` in @repo/types (the rule lives there); only the tones are here.
 * Demo keeps the orange it had as a status, so a demo reads the same as before.
 */
export const COMPANY_SEGMENT_STATUSES = {
  signed_up: {
    label: COMPANY_SEGMENT_LABELS.signed_up,
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  demo: {
    label: COMPANY_SEGMENT_LABELS.demo,
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
  },
  example: {
    label: COMPANY_SEGMENT_LABELS.example,
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  test: {
    label: COMPANY_SEGMENT_LABELS.test,
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  internal: {
    label: COMPANY_SEGMENT_LABELS.internal,
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  archived: {
    label: COMPANY_SEGMENT_LABELS.archived,
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/** VRG-718 — the Activated milestone's yes/no facet (the cell itself shows the date). */
export const COMPANY_ACTIVATED_STATUSES = {
  activated: {
    label: 'Activated',
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  not_activated: {
    label: 'Not activated',
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

export const COMPANY_USER_STATUSES = {
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  invited: {
    label: "Invited",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  pending: {
    label: "Pending",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  draft: {
    label: "Draft",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  inactive: {
    label: "Archived",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

export const INVENTORY_STATUSES = {
  plenty: {
    label: "Plenty",
    bar: palette.green[700],
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  low: {
    label: "Running low",
    bar: palette.yellow[500],
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  critical: {
    label: "Need to order",
    bar: palette.red[600],
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

export const AUTO_ORDER_STATUSES = {
  on: {
    label: "On",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  off: {
    label: "Off",
    color: palette.grey[600],
    borderColor: palette.grey[200],
    backgroundColor: palette.grey[100],
  },
};

export const OWNER_STATUSES = {
  pending: {
    label: "Pending",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  inactive: {
    label: "Inactive",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  draft: {
    label: "Draft",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  archived: {
    label: "Archived",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

/**
 * VRG-727 — a company's money status (`payingNowStatus`, @repo/types gtm/paying.ts), the chip
 * on Sales → Companies, Deals and the company drawer, and on Accounts → Companies. The labels
 * come from PAYING_NOW_STATUS_LABELS; each chip explains itself from the glossary.
 */
export const PAYING_NOW_CHIP_STATUSES = {
  paying_now: {
    label: PAYING_NOW_STATUS_LABELS.paying_now,
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
    glossaryId: "paying-now",
  },
  not_paying: {
    label: PAYING_NOW_STATUS_LABELS.not_paying,
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
    glossaryId: "paying-now",
  },
  never_paid: {
    label: PAYING_NOW_STATUS_LABELS.never_paid,
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
    glossaryId: "paying-now",
  },
};

export const PLAN_STATUSES = {
  current: {
    label: "Current",
    color: palette.brand[700],
    borderColor: palette.brand[200],
    backgroundColor: palette.brand[100],
  },
  // VRG-372: a company sitting on a plan the catalog no longer sells
  // (stripe_subscription_products.active = false) — flagged, never hidden.
  retired: {
    label: "Retired",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

export const INVOICE_STATUSES = {
  paid: {
    label: "Paid",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  open: {
    label: "Open",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  draft: {
    label: "Draft",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  failed: {
    label: "Failed",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
  refunded: {
    label: "Refunded",
    // Orange (money returned) — distinct from open/blue, which it clashed with.
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  void: {
    label: "Void",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  uncollectible: {
    label: "Uncollectible",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
};

// Order PAYMENT (financial) status — distinct from INVOICE_STATUSES. These are
// the Shopify order `financialStatus` values (an order-level field), surfaced as
// a badge on the tag-shop order detail. Kept separate from invoices on purpose.
export const PAYMENT_STATUSES = {
  paid: {
    label: "Paid",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  pending: {
    label: "Pending",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  authorized: {
    label: "Authorized",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  partially_paid: {
    label: "Partially paid",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  partially_refunded: {
    label: "Partially refunded",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  refunded: {
    label: "Refunded",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  voided: {
    label: "Voided",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

export const ORDER_STATUSES = {
  // Chrome/neutral (palette.gray), not orange — this is the default starting
  // state (nothing has happened yet), not a warning. Mirrors palette.gray's
  // use for PAYMENT_STATUSES.voided.
  unfulfilled: {
    label: "Unfulfilled",
    term: "order-status-unfulfilled",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  partially_fulfilled: {
    label: "Partially Fulfilled",
    term: "order-status-partially-fulfilled",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  fulfilled: {
    label: "Fulfilled",
    term: "order-status-fulfilled",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  pending: {
    label: "Pending",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  shipped: {
    label: "Shipped",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  in_transit: {
    label: "In Transit",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  delivered: {
    label: "Delivered",
    color: palette.purple[700],
    borderColor: palette.purple[200],
    backgroundColor: palette.purple[100],
  },
  cancelled: {
    label: "Cancelled",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

export const SUBSCRIPTION_STATUSES = {
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  late: {
    label: "Late",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  overdue: {
    label: "Overdue",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  suspended: {
    label: "Suspended",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

// VERA-1096: Internal subscription list — status chips matching enum_company_subscription_status
export const INTERNAL_SUBSCRIPTION_STATUSES = {
  active: {
    label: "Active",
    term: "subscription-status-active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  trialing: {
    label: "Trialing",
    term: "subscription-status-trialing",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  past_due: {
    label: "Past due",
    term: "subscription-status-past-due",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  incomplete: {
    label: "Incomplete",
    term: "subscription-status-incomplete",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  incomplete_expired: {
    label: "Incomplete expired",
    term: "subscription-status-incomplete-expired",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  paused: {
    label: "Paused",
    term: "subscription-status-paused",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  canceled: {
    label: "Canceled",
    term: "subscription-status-canceled",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
  unpaid: {
    label: "Unpaid",
    term: "subscription-status-unpaid",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

export const PAYMENT_METHOD_STATUSES = {
  default: {
    label: "Default",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
};

export const ADDRESS_STATUSES = {
  default: {
    label: "Default",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
};

export const INTEGRATION_STATUSES = {
  connected: {
    label: "Connected",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  primary: {
    label: "Primary",
    color: palette.brand[700],
    borderColor: palette.brand[200],
    backgroundColor: palette.brand[100],
  },
  verified: {
    label: "Verified",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  // The Vera subdomain is hosted by us and created at signup, so it can't be
  // "not connected" the way an integration or a customer's own site can. Neutral
  // on purpose: it's a statement of fact, not a success the user achieved.
  alwaysAvailable: {
    label: "Always available",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  unverified: {
    label: "Not verified",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  checking: {
    label: "Checking…",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

// VERA-362 — the custom email sending domain's verification status, on the
// Settings → Features "Custom email sending domain" card.
export const EMAIL_DOMAIN_STATUSES = {
  pending: {
    label: "Pending",
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  verified: {
    label: "Verified",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  failed: {
    label: "Failed",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

export const ROLE_STATUSES = {
  default: {
    // "Built-in" renamed to "Default" (pairs with "Custom" — the Role select's
    // rightLabel and the "Add custom role" action read as Default/Custom). The
    // old label was "Built-in · Default"; the standalone "· Default" is now
    // redundant with "Default" itself, so it's dropped rather than doubled up.
    label: 'Default',
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  modified: {
    label: 'Default · Modified',
    color: palette.orange[700],
    borderColor: palette.orange[200],
    backgroundColor: palette.orange[100],
  },
  custom: {
    label: 'Custom',
    color: palette.purple[700],
    borderColor: palette.purple[200],
    backgroundColor: palette.purple[100],
  },
};

export const INSTALLATION_STATUSES = {
  installed: {
    label: "Installed",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
};

export const INTERNAL_USER_STATUSES = {
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  invited: {
    label: "Invited",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  inactive: {
    label: "Archived",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

// VERA-790: internal "Tags" list — status chips matching tags.status
export const TAG_STATUSES = {
  active: {
    label: "Active",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  disabled: {
    label: "Disabled",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  retired: {
    label: "Retired",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

// VERA-790: internal "Packages" list — graded fill status derived from the row's tag count
// (`quantity` = actual tags in the box) vs. the box's expected capacity (the selected variant's
// quantity, e.g. "Qty 100"). Empty (0) / Partial (some, below target) / Filled (>= target). When
// no variant is assigned yet there's no target, so it falls back to Empty (0) / Filled (>0).
export const PACKAGE_FILL_STATUSES = {
  filled: {
    label: "Filled",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  partial: {
    label: "Partial",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  empty: {
    label: "Empty",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

// VRG-92: Signups waitlist status chips
export const SIGNUP_STATUSES = {
  pending: {
    label: "Pending",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  granted: {
    /**
     * VRG-Pricing.Access.Gate — the LABEL is "Allowed"; the KEY stays `granted`.
     *
     * The chip used to read "Granted" while no action on the row's menu was
     * called "Grant" — the action is "Allow" / "Allow & invite". So the column
     * named the outcome of an action the operator could not find, and once a
     * SECOND grant landed on the same row ("Grant pricing access", which is a
     * different thing entirely) the word stopped identifying which grant it
     * meant.
     *
     * ⚠️ The key is the wire value. `signups.status` is `'granted'` in the
     * database, `SignupEntry['status']` in `@domain/types`, and the literal the
     * row kebab branches on (`row.status === 'granted'`). Renaming it would be a
     * migration plus an API change to fix a word on a chip. Only the display
     * string moves.
     */
    label: "Allowed",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  rejected: {
    label: "Rejected",
    color: palette.red[700],
    borderColor: palette.red[200],
    backgroundColor: palette.red[100],
  },
};

/**
 * VRG-Pricing.Access.Gate — whether a waitlist entry may read Vera's real price
 * list on the marketing site's gated `/pricing`.
 *
 * A THIRD map on this page, and the reason it is not folded into
 * `SIGNUP_STATUSES` is that the two answer different questions about the same
 * row: that one is "may this person create a Vera account", this one is "may
 * this person see what it costs". They are granted and revoked independently on
 * purpose (see `GrantPricingAccessUseCase`), so a row can be Allowed with no
 * pricing access, or hold pricing access while still Pending.
 *
 * ⚠️ NOT stored. Both values are DERIVED from the two timestamps on the signups
 * row by `pricingAccessIsLive`, never a third boolean that could disagree with
 * the data it describes.
 *
 * ONLY TWO ENTRIES, and the missing third is deliberate: "never granted" renders
 * as the em-dash empty value, not as a chip. A pill saying "None" on every row
 * that was never considered would put a status token on rows that have no
 * status, which is how a column of chips stops carrying information.
 *
 * `revoked` is GREY, not red. Red is the failure tone this file already spends
 * on `rejected` one map up, and revoking pricing access is not a rejection of
 * the person. It is set-removal (§38) and reversible in one click, so it reads
 * as "not currently on" rather than as a fault. Same argument
 * `SIGNUP_CONVERSION_STATUSES` makes below for not painting a lead who has not
 * signed up in amber.
 */
export const SIGNUP_PRICING_ACCESS_STATUSES = {
  granted: {
    label: "Granted",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  revoked: {
    label: "Revoked",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * VRG-545: whether an ALLOWLISTED email ever became a customer.
 *
 * Distinct from `SIGNUP_STATUSES` above, which is the waitlist's REVIEW state
 * (has staff let this person in). This is what happened AFTER staff let them in,
 * which is the question the Allowlist tab could not answer at all before.
 *
 * Three states because the middle one is real: an account with no company is a
 * half-finished signup, and folding it into either neighbour reports something
 * false. `SignupAllowlistConversionStatus` (apps/api domain) derives it, and its
 * docblock carries the reasoning for where the line sits — keep these labels in
 * step with it.
 *
 * Colour reads as progress, not as pass/fail: grey = nothing happened yet, blue =
 * in motion, green = arrived. Amber/red would frame a lead who has not signed up
 * as a fault, which it is not — most never will.
 */
export const SIGNUP_CONVERSION_STATUSES = {
  not_signed_up: {
    label: "Not signed up",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
  signed_up: {
    label: "Signed up",
    color: palette.blue[700],
    borderColor: palette.blue[200],
    backgroundColor: palette.blue[100],
  },
  converted: {
    label: "Converted",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
};

/**
 * VRG-639: whether a converted company's `became_client` event reached the
 * website's PostHog (`companies.became_client_posthog_status`). Only `sent` is
 * rendered — as a second chip beside "Converted" on the Allowlist tab. The skipped
 * states (no PostHog person / consent withdrawn) are NOT shown: they are the
 * visitor's own privacy choice, and a row that reads "Not tracked" would invite
 * someone to go and fix it.
 */
export const SIGNUP_TRACKING_STATUSES = {
  sent: {
    label: "Tracked",
    // `shadedPalette`, not `palette`: adds nothing to the TS7053 baseline.
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
};

// VRG-138 Phase 3 (VERA-891): a shipment's label-purchase state
// (`order_shipments.label_status`), shown as a chip per shipment card.
// Uses `shadedPalette` (declared at the top of this file) so it adds nothing to
// the TS7053 baseline; see that declaration for why it is not local to here.
export const SHIPMENT_LABEL_STATUSES = {
  none: {
    label: "No label",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  pending: {
    label: "Purchasing",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  purchased: {
    label: "Purchased",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  failed: {
    label: "Failed",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
};

// VRG-247: shared pill tone for the Companies table "Comped" column. NOT a
// bounded status map — a comp can be any 1-24 months (the "Custom" duration on
// CompSubscriptionModal), so the cell builds its own single-entry statuses map
// keyed by the actual duration and spreads this tone in.
export const COMPANY_COMP_TONE = {
  color: shadedPalette.brand[700],
  borderColor: shadedPalette.brand[200],
  backgroundColor: shadedPalette.brand[100],
};

// VRG-247: internal Companies list "Granted" column — total activation credits
// granted to a company. Unlike the maps above this isn't a bounded enum (the total is
// an unbounded count), so the cell builds a single-entry StatusChip statuses object
// keyed by the total itself at render time; this constant is just the shared tone.
export const COMPANY_GRANTED_ACTIVATIONS_TONE = {
  color: shadedPalette.blue[700],
  borderColor: shadedPalette.blue[200],
  backgroundColor: shadedPalette.blue[100],
};

// VRG-226. `palette`'s MUI augmentation types each colour scale as
// PaletteColorOptions, which carries no numeric index signature — so every
// `palette.gray[700]` above is a TS7053 under a strict checker (264 of them in
// this file). Fixing that is not this epic's job, but new code should not add to
// the pile, so the maps below go through `tone`: the same object with the index
// signature the scales actually have.
const tone = palette as unknown as Record<string, Record<string, string>>;

// VRG-226 — support requests. `waiting_on_customer` is amber rather than neutral
// on purpose: it is the one state where the ball is in the brand's court, and a
// list that renders it the same as `open` hides the fact that nothing will move
// until they reply.
// These labels are the same strings as `SUPPORT_REQUEST_STATUS_LABELS` in
// @repo/types, which is what the API writes into a thread's system notes — the
// chip and the note sit one above the other, so they have to agree. They are
// repeated rather than imported because `apps/platform` does not depend on
// @repo/types today (its two existing imports from it are type-only and do not
// resolve), and adding a runtime import from an undeclared package would fail at
// module resolution. If that dependency is ever added, delete these strings and
// read them from the shared map.
export const SUPPORT_REQUEST_STATUSES = {
  open: {
    label: "Open",
    term: "support-status-open",
    color: tone.blue[700],
    borderColor: tone.blue[200],
    backgroundColor: tone.blue[100],
  },
  in_progress: {
    label: "In progress",
    term: "support-status-in-progress",
    color: tone.cyan[700],
    borderColor: tone.cyan[200],
    backgroundColor: tone.cyan[100],
  },
  waiting_on_customer: {
    // The one deliberate divergence from the shared label: to a brand, the ball
    // is in their court. Vera's map below puts the neutral wording back.
    label: "Waiting on you",
    term: "support-status-waiting-on-you",
    color: tone.orange[700],
    borderColor: tone.orange[200],
    backgroundColor: tone.orange[100],
  },
  resolved: {
    label: "Resolved",
    term: "support-status-resolved",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
  closed: {
    label: "Closed",
    term: "support-status-closed",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
};

// The same statuses from Vera's side of the desk. Only the copy differs — a
// separate map rather than a conditional label, because a status chip that reads
// differently depending on who is looking is worth being explicit about.
export const SUPPORT_REQUEST_STATUSES_INTERNAL = {
  ...SUPPORT_REQUEST_STATUSES,
  waiting_on_customer: {
    ...SUPPORT_REQUEST_STATUSES.waiting_on_customer,
    label: "Waiting on customer",
  },
};

// VRG-226 — board items.
export const BOARD_ITEM_STATUSES = {
  under_review: {
    label: "Under review",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  planned: {
    label: "Planned",
    color: tone.blue[700],
    borderColor: tone.blue[200],
    backgroundColor: tone.blue[100],
  },
  in_progress: {
    label: "Building",
    color: tone.brand[700],
    borderColor: tone.brand[200],
    backgroundColor: tone.brand[100],
  },
  shipped: {
    label: "Shipped",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
  declined: {
    label: "Not planned",
    color: tone.red[700],
    borderColor: tone.red[200],
    backgroundColor: tone.red[100],
  },
  merged: {
    label: "Merged",
    color: tone.purple[700],
    borderColor: tone.purple[200],
    backgroundColor: tone.purple[100],
  },
};

// VRG-332 — an announcement's LIFECYCLE state, for the internal authoring list.
//
// A pill, and specifically a `StatusChip` pill, because draft → published is a
// state the record MOVES through — which is exactly what the pill vocabulary means
// on every other list in this app. The Kind beside it is a fixed classification and
// gets the BADGE treatment instead (`AnnouncementKindBadge`); the split is the same
// one `SupportPage` already makes between a request's status and its type, and it is
// the whole reason the two chips do not read as two states of one thing.
//
// There is no `status` column behind this. `published_at` is the state (see the
// table's schema comment — a nullable timestamp rather than an enum, so a row cannot
// be `published` with no date), so the list derives the key from it.
//
// Green for published rather than the brand tint it had as bare text: published is a
// terminal, everything-is-fine state, which is what green means in every other map in
// this file (`resolved`, `shipped`, `active`). Grey for draft, matching `closed` and
// `under_review` — not-yet-live, not a problem.
export const ANNOUNCEMENT_STATUSES = {
  draft: {
    label: "Draft",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  published: {
    label: "Published",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
};

// VRG-462 — a registered feed's polling health (AC8).
//
// Three states, and the middle one is the point: "Quiet" is a source that polls
// cleanly but has produced nothing. Without it, a working-but-empty feed and a
// broken one both render as an absence, which is exactly the confusion the audit
// trail exists to remove.
export const MARKET_SOURCE_HEALTH = {
  healthy: {
    label: "Healthy",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
  quiet: {
    label: "Quiet",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  failing: {
    label: "Failing",
    color: tone.red[700],
    borderColor: tone.red[200],
    backgroundColor: tone.red[100],
  },
  paused: {
    label: "Paused",
    color: tone.yellow[700],
    borderColor: tone.yellow[200],
    backgroundColor: tone.yellow[100],
  },
};

// VRG-461 — an operator's review state for one market-data corpus item.
export const MARKET_ITEM_STATUSES = {
  unreviewed: {
    label: "Unreviewed",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  kept: {
    label: "Kept",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
  rejected: {
    label: "Rejected",
    color: tone.red[700],
    borderColor: tone.red[200],
    backgroundColor: tone.red[100],
  },
};

// VRG-445 — the Stats-tab review table's status column. Computed from
// `approvedAt`/`rejectedAt` client-side (see `askClaimToStatus`), never a stored
// column — the two timestamps are the source of truth and are kept mutually
// exclusive server-side (AskClaimsService.reject clears approved_at).
export const MARKET_STAT_STATUSES = {
  pending: {
    label: "Pending",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  approved: {
    label: "Approved",
    color: tone.green[700],
    borderColor: tone.green[200],
    backgroundColor: tone.green[100],
  },
  rejected: {
    label: "Rejected",
    color: tone.red[700],
    borderColor: tone.red[200],
    backgroundColor: tone.red[100],
  },
};

// VRG-226 — request types, shown as a chip in the list so a bug and a feature
// suggestion are distinguishable at a glance.
export const SUPPORT_REQUEST_TYPES = {
  support: {
    label: "Support",
    term: "support-request-type",
    color: tone.blue[700],
    borderColor: tone.blue[200],
    backgroundColor: tone.blue[100],
  },
  bug: {
    label: "Bug",
    term: "support-request-type",
    color: tone.red[700],
    borderColor: tone.red[200],
    backgroundColor: tone.red[100],
  },
  feature: {
    label: "Feature",
    term: "support-request-type",
    color: tone.purple[700],
    borderColor: tone.purple[200],
    backgroundColor: tone.purple[100],
  },
};

/**
 * VRG-726 — the internal queue's Source facet: automatic report nobody spoke to, an automatic
 * report a person also told us about, or a person's own report. Labels from
 * `utils/supportReportSource` (the classifier, unit-tested under node); colours here with the
 * rest. Grey for "logged only" because nobody is waiting on it; amber for the incident a person
 * is waiting on AND the logs can explain, the row worth opening first; blue for an ordinary
 * request, matching Support's own badge.
 */
export const SUPPORT_REPORT_SOURCES = {
  auto: {
    label: SUPPORT_REPORT_SOURCE_LABELS.auto,
    term: "support-report-source",
    color: tone.gray[700],
    borderColor: tone.gray[200],
    backgroundColor: tone.gray[100],
  },
  auto_with_user: {
    label: SUPPORT_REPORT_SOURCE_LABELS.auto_with_user,
    term: "support-report-source",
    color: tone.orange[700],
    borderColor: tone.orange[200],
    backgroundColor: tone.orange[100],
  },
  user: {
    label: SUPPORT_REPORT_SOURCE_LABELS.user,
    term: "support-report-source",
    color: tone.blue[700],
    borderColor: tone.blue[200],
    backgroundColor: tone.blue[100],
  },
};

/**
 * VRG-378: how a request's SOURCE reads to a human.
 *
 * `platform` is present for completeness but is not rendered anywhere today — a
 * request viewed inside the platform is from the platform unless it says
 * otherwise, so the label only earns its place when it is not the default.
 */
export const SUPPORT_SOURCE_LABELS: Record<string, string> = {
  platform: 'Platform',
  scanner_app: 'Scanner app',
  certificates_app: 'Certificate page',
}

/**
 * VRG-455 — the lifecycle of a recorded sale (a "pending ownership" record).
 *
 * Three values, and they are not a progress bar: `pending` is the resting state
 * and stays that way indefinitely by design (the record has no TTL — reminders
 * chase it, nothing expires it), `resolved` means the buyer's scan bound a real
 * certificate and completed the transfer, and `voided` means the brand retracted
 * the sale (a refund, a cancellation, a mistyped row).
 *
 * `pending` is YELLOW rather than grey because it is the state a brand may need
 * to act on — the buyer has bought something and has not yet got it in their
 * account — and grey would read as "nothing to see here" for the only status
 * with an outstanding obligation attached.
 *
 * No `glossaryId` on any of the three yet. The pending-ownership term IS a term
 * of art and owes a glossary entry (§59), but the wording is a product decision
 * and the entry has not been authored; wiring a chip to a term that does not
 * exist renders a tooltip pointing at nothing.
 */
export const PENDING_SALE_STATUSES = {
  pending: {
    label: "Pending",
    color: palette.yellow[700],
    borderColor: palette.yellow[200],
    backgroundColor: palette.yellow[100],
  },
  resolved: {
    label: "Resolved",
    color: palette.green[700],
    borderColor: palette.green[200],
    backgroundColor: palette.green[100],
  },
  voided: {
    label: "Voided",
    color: palette.gray[700],
    borderColor: palette.gray[200],
    backgroundColor: palette.gray[100],
  },
};

/**
 * VRG-455 — where a package of tags physically went.
 *
 * Labels, and the CONSEQUENCE each one carries, in the operator's terms rather
 * than the enum's. The consequence is the whole point of the control: a brand
 * setting this is not filling in metadata, they are deciding whether a buyer's
 * scan is allowed to claim the certificates for the tags in that box.
 *
 * `unknown` is the default every existing package sits on, so its description
 * has to say what that costs rather than reading as a neutral "not set".
 */
export const CONSIGNMENT_DESTINATIONS = {
  retail_or_held: {
    label: "Retail or held stock",
    description:
      "A shop floor, a retail partner, or your own warehouse. A buyer's scan cannot claim the certificates for tags in this box, so stock on a shelf stays yours.",
  },
  direct_to_consumer: {
    label: "Shipped to a buyer",
    description:
      "Fulfilled straight to a customer, including dropship. A buyer with a recorded sale can claim the certificate for the item they scan.",
  },
  unknown: {
    label: "Not recorded",
    description:
      "Where this box went has not been recorded. Its certificates stay claimable by a buyer's scan, which is the right default for untracked fulfilment. Set it if the box went to retail.",
  },
} as const;

export type ConsignmentDestinationValue = keyof typeof CONSIGNMENT_DESTINATIONS

/**
 * Site-gate invites (/internal/signups → Site invites). The API derives the key
 * (`siteGateInviteStatus`: revoked → expired → unused → active) so this map
 * only names and colours it.
 *
 * Green means the link is live AND has let somebody in; blue is live but never
 * opened, the same "nothing happened yet, but it can" reading
 * `SIGNUP_CONVERSION_STATUSES` gives "Signed up". Expired and Revoked are both
 * GREY, not red, for the reason `SIGNUP_PRICING_ACCESS_STATUSES.revoked` gives:
 * ending an invite is set-removal, not a fault.
 */
export const SITE_GATE_INVITE_STATUSES = {
  active: {
    label: "Active",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  unused: {
    label: "Never used",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  expired: {
    label: "Expired",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  revoked: {
    label: "Revoked",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * Access (/internal/access; "People" until VRG-727) — the one vocabulary all three access columns share
 * (Website / Pricing / Platform). The API derives the key per column
 * (apps/api/src/features/people-access/people-view.ts documents how each
 * system's real states map onto it); this only names and colours it.
 *
 * `none` is deliberately ABSENT: like the pricing column before it, "never
 * granted" renders as the em-dash empty value rather than a pill on every row.
 * Amber is the one colour that asks for attention, and only `requested` uses it.
 */
export const PEOPLE_ACCESS_STATUSES = {
  requested: {
    label: "Requested",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  invited: {
    label: "Invited",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  granted: {
    label: "Granted",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  expired: {
    label: "Expired",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  revoked: {
    label: "Revoked",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * VRG-672: a Traction channel's status on `/internal/gtm`. The five values and their
 * meanings are the canon's (`vera-inc/gtm/channels/README.md`), and the colours follow its
 * emoji key so the page and the README read the same at a glance: 🟢 Focus, 🔵 Active,
 * 🟡 Planned, ⚪ Proposed, ⚫ Not now. Proposed is purple rather than white because a white
 * chip on a white table has no edge; Not now is grey because it is a decision to skip,
 * not a fault.
 *
 * `glossaryId` on each (VRG-684): the definitions VRG-672 drafted, now in the internal glossary.
 */
export const GTM_CHANNEL_STATUSES = {
  focus: {
    label: "Focus",
    glossaryId: "gtm-channel-status-focus",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  active: {
    label: "Active",
    glossaryId: "gtm-channel-status-active",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  planned: {
    label: "Planned",
    glossaryId: "gtm-channel-status-planned",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  proposed: {
    label: "Proposed",
    glossaryId: "gtm-channel-status-proposed",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  not_now: {
    label: "Not now",
    glossaryId: "gtm-channel-status-not-now",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * VRG-673: the trade-show calendar's three judgement columns. Each mirrors a field of
 * `gtm/channels/trade-shows/events.csv`, and the labels are the README's own words.
 *
 * FIT is the researcher's judgement, "not sourced" (README): green = small and mid-size
 * founder-led brands can realistically attend or exhibit; yellow = mixed; grey = mostly large
 * houses or invitation-only. Grey, not red, for the last: a large-house show is a few
 * introductions rather than a booth, which is a different use, not a bad event.
 */
export const TRADE_SHOW_FIT = {
  small: {
    label: "Small brands",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  mixed: {
    label: "Mixed",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  large: {
    label: "Large houses",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  unrated: {
    label: "Not rated",
    color: shadedPalette.gray[500],
    borderColor: shadedPalette.gray[200],
    backgroundColor: "transparent",
  },
};

/** Whether a show's date was confirmed on the organizer's own page (`date_status`). */
export const TRADE_SHOW_DATE_STATUSES = {
  confirmed: {
    label: "Confirmed",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  unconfirmed: {
    label: "Unconfirmed",
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
  },
};

/** `plan_to_attend`. Read-only in v1: marking a show happens in the CSV until VRG-673's follow-up. */
export const TRADE_SHOW_PLAN = {
  yes: {
    label: "Attending",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  not_marked: {
    label: "Not marked",
    color: shadedPalette.gray[600],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * VRG-674: an article's state on `/internal/gtm/articles` — its publication (owned by vera-2
 * since VRG-685; Publish on that page takes it live) combined with Spencer's approval recorded in
 * vera-2. Derived by `articleState` in `GtmArticlesPage/articleModel.ts`. Colour reads as
 * progress: yellow = a decision is owed, blue = approved and waiting to be published, green =
 * live; orange = an approval exists but for an earlier version of the words. The other three
 * states' glossary entries were approved by Spencer 2026-10-08 (VRG-685); every chip carries one.
 */
export const ARTICLE_STATES = {
  needs_review: {
    label: "Needs review",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
    glossaryId: "article-status-needs-review",
  },
  changed: {
    label: "Changed since approval",
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
    glossaryId: "article-status-changed",
  },
  awaiting_publish: {
    label: "Awaiting publish",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
    glossaryId: "article-status-awaiting-publish",
  },
  published: {
    label: "Published",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
    glossaryId: "article-status-published",
  },
};

/**
 * VRG-655 — a referral as its SENDER sees it (`MemberReferralStatus`, VRG-654).
 *
 * The keys are the API's member statuses; the API already folds every side-state a sender
 * must not learn about (already a customer, already waiting, another brand's) into
 * `invited`, which reads "Sent" here — indistinguishable from an invitation still waiting.
 *
 * Colour reads as PROGRESS, the same scheme `SIGNUP_CONVERSION_STATUSES` argues for: grey =
 * nothing yet, blue = in motion, purple = on Vera, green = paying. "Not eligible" is grey,
 * not red: a self-referral is not a fault, it simply does not count.
 *
 * `glossaryId` on every entry (§59): each label is a term of art with a definition the
 * reader can hover for.
 */
export const REFERRAL_MEMBER_STATUSES = {
  invited: {
    label: "Sent",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
    glossaryId: "referral-status-sent",
  },
  signed_up: {
    label: "Signed up",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
    glossaryId: "referral-status-signed-up",
  },
  approved: {
    label: "Approved",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
    glossaryId: "referral-status-approved",
  },
  joined: {
    label: "Joined",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
    glossaryId: "referral-status-joined",
  },
  paying: {
    label: "Paying",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
    glossaryId: "referral-status-paying",
  },
  capped: {
    label: "Limit reached",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
    glossaryId: "referral-status-limit-reached",
  },
  ended: {
    label: "Ended",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
    glossaryId: "referral-status-ended",
  },
  ineligible: {
    label: "Not eligible",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
    glossaryId: "referral-status-not-eligible",
  },
};

/**
 * VRG-658 — a referral's RAW status, for Vera staff only (`referrals.status`). Unredacted:
 * staff see `ineligible` and `superseded` as themselves, with the reason alongside.
 *
 * Labels follow the brand-facing map where the state is the same one (`granted` is what a
 * brand sees as "Approved"), so staff and the brand can talk about one referral in one word.
 */
export const REFERRAL_STAFF_STATUSES = {
  sent: {
    label: "Sent",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  signed_up: {
    label: "Signed up",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  granted: {
    label: "Approved",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  converted: {
    label: "Joined",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  paying: {
    label: "Paying",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  capped: {
    label: "Limit reached",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  ended: {
    label: "Ended",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  ineligible: {
    label: "Ineligible",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
  superseded: {
    label: "Superseded",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/**
 * VRG-679 — an outreach target's status (`OUTREACH_TARGET_STATUSES` in @repo/types), coloured
 * as PROGRESS like the referral statuses: grey = nothing yet, blue = in motion, purple = they
 * answered. Declined is red: the one terminal "no".
 *
 * VRG-684 added three later stages, so the warm end now runs through four hues, one per step and
 * none shared, because a campaign's per-status counts sit side by side: meeting booked = yellow
 * (scheduled, not yet happened), meeting held = orange, pilot = green, LOI signed = brand (the
 * outcome the whole channel exists for).
 */
export const OUTREACH_TARGET_STATUS_CHIPS = {
  to_contact: {
    label: "To contact",
    glossaryId: "outreach-status-to-contact",
    color: shadedPalette.grey[600],
    borderColor: shadedPalette.grey[200],
    backgroundColor: shadedPalette.grey[100],
  },
  sent: {
    label: "Sent",
    glossaryId: "outreach-status-sent",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  replied: {
    label: "Replied",
    glossaryId: "outreach-status-replied",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  meeting_booked: {
    label: "Meeting booked",
    glossaryId: "outreach-status-meeting-booked",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  meeting_held: {
    label: "Meeting held",
    glossaryId: "outreach-status-meeting-held",
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
  },
  pilot: {
    label: "Pilot",
    glossaryId: "outreach-status-pilot",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  loi_signed: {
    label: "LOI signed",
    glossaryId: "outreach-status-loi-signed",
    color: shadedPalette.brand[700],
    borderColor: shadedPalette.brand[200],
    backgroundColor: shadedPalette.brand[100],
  },
  declined: {
    label: "Declined",
    glossaryId: "outreach-status-declined",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
};

/** VRG-679 — an outreach campaign's type. "Field visit" is the in-person kind. */
export const OUTREACH_CAMPAIGN_TYPE_CHIPS = {
  email: {
    label: "Email outreach",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  in_person: {
    label: "Field visit",
    glossaryId: "field-visit",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
};

/**
 * /internal/integrations — the five `integration_requests.status` values, as the Roadmap
 * tab renders and filters them. Keys are the DB enum values verbatim.
 */
const chip = (shade: string) => ({
  color: shadedPalette[shade][700],
  borderColor: shadedPalette[shade][200],
  backgroundColor: shadedPalette[shade][100],
});

export const INTEGRATION_REQUEST_STATUSES = {
  pending_review: { label: "Pending review", ...chip("gray") },
  planned: { label: "Planned", ...chip("blue") },
  shipping_soon: { label: "Shipping soon", ...chip("purple") },
  shipped: { label: "Shipped", ...chip("green") },
  declined: { label: "Declined", ...chip("red") },
};

/**
 * Roadmap FIT grade (product/integrations-landscape.md §2): a judgement call, not a
 * measurement. Keys are the stored values (ASCII "A-"); labels use a true minus sign.
 * Green = build first, blue = a strong next step, yellow = later, gray = skip.
 * `unrated` is the "no grade yet" bucket so the filter can find ungraded rows.
 */
export const INTEGRATION_FIT_GRADES = {
  A: { label: "A", ...chip("green") },
  "A-": { label: "A−", ...chip("green") },
  "B+": { label: "B+", ...chip("blue") },
  B: { label: "B", ...chip("blue") },
  "B-": { label: "B−", ...chip("blue") },
  C: { label: "C", ...chip("yellow") },
  D: { label: "D", ...chip("gray") },
  unrated: {
    label: "Not graded",
    color: shadedPalette.gray[500],
    borderColor: shadedPalette.gray[200],
    backgroundColor: "transparent",
  },
};

/** GTM → Social: a draft's place in the weekly approval pass. Nothing posts unapproved; for LinkedIn, Scheduled and Posted are bookkeeping a person does, and an approved, scheduled Instagram draft is published by the API worker once a connector is on. */
export const SOCIAL_DRAFT_STATUSES = {
  draft: {
    label: "Draft",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  needs_fixes: {
    label: "Needs fixes",
    color: shadedPalette.orange[700],
    borderColor: shadedPalette.orange[200],
    backgroundColor: shadedPalette.orange[100],
  },
  approved: {
    label: "Approved",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  rejected: {
    label: "Rejected",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
  scheduled: {
    label: "Scheduled",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  posted: {
    label: "Posted",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
};

/** GTM → Social: where an Instagram draft is in the publish flow. Dry run = previewed only, nothing sent. */
export const SOCIAL_PUBLICATION_STATUSES = {
  queued: {
    label: "Queued",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  publishing: {
    label: "Publishing",
    color: shadedPalette.blue[700],
    borderColor: shadedPalette.blue[200],
    backgroundColor: shadedPalette.blue[100],
  },
  published: {
    label: "Published",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  failed: {
    label: "Failed",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
  dry_run: {
    label: "Dry run",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  cancelled: {
    label: "Cancelled",
    color: shadedPalette.gray[500],
    borderColor: shadedPalette.gray[200],
    backgroundColor: "transparent",
  },
};

/** GTM → Social: the Instagram connector's mode. Live is the only one that reaches Instagram. */
export const SOCIAL_PUBLISH_MODES = {
  off: {
    label: "Publishing off",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
  dry_run: {
    label: "Dry run",
    color: shadedPalette.purple[700],
    borderColor: shadedPalette.purple[200],
    backgroundColor: shadedPalette.purple[100],
  },
  live: {
    label: "Live",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
};

/** GTM → Social: a stat in the registry. Only Approved stats can be cited by a draft. */
export const SOCIAL_STAT_STATUSES = {
  draft: {
    label: "Draft",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  approved: {
    label: "Approved",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  retired: {
    label: "Retired",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

/** GTM → Social: how much of a stat's freshness window is left, computed from its published date (`statFreshness` in GtmSocialPage/socialModel.ts). No published date reads Needs date (not Fresh, not Stale): there is no window to judge. */
export const SOCIAL_STAT_FRESHNESS = {
  fresh: {
    label: "Fresh",
    color: shadedPalette.green[700],
    borderColor: shadedPalette.green[200],
    backgroundColor: shadedPalette.green[100],
  },
  expiring: {
    label: "Expiring",
    color: shadedPalette.yellow[700],
    borderColor: shadedPalette.yellow[200],
    backgroundColor: shadedPalette.yellow[100],
  },
  stale: {
    label: "Stale",
    color: shadedPalette.red[700],
    borderColor: shadedPalette.red[200],
    backgroundColor: shadedPalette.red[100],
  },
  undated: {
    label: "Needs date",
    color: shadedPalette.gray[700],
    borderColor: shadedPalette.gray[200],
    backgroundColor: shadedPalette.gray[100],
  },
};

// Uses the existing shade-only `chip` above and adds the label.
const labelledChip = (label: string, shade: string, glossaryId?: string) => ({
  label,
  ...chip(shade),
  ...(glossaryId ? { glossaryId } : {}),
});

/**
 * VRG-740 — a content idea on GTM → Articles → Ideas (`@repo/types` CONTENT_IDEA_STATUSES). Colour
 * reads as progress, like the Articles states: gray = not started, yellow = being shaped, orange =
 * being written, blue = approved and waiting to publish, green = live, red = said no.
 */
export const CONTENT_IDEA_STATES = {
  idea: labelledChip("Idea", "gray", "content-idea-status-idea"),
  brief: labelledChip("Brief", "yellow", "content-idea-status-brief"),
  draft: labelledChip("Draft", "orange", "content-idea-status-draft"),
  approved: labelledChip("Draft approved", "blue", "content-idea-status-approved"),
  published: labelledChip("Published", "green", "content-idea-status-published"),
  rejected: labelledChip("Rejected", "red", "content-idea-status-rejected"),
};

/*
  VRG-752 — `VERAFIED_GUEST_STATES` is gone with the second guest list. It held VRG-740's own
  outreach ladder (suggested → contacted → booked → recorded / declined) for a table that no longer
  exists; the one guest list runs on `@repo/types` `VERAFIED_GUEST_STATUSES` (ten states, from
  not_invited to published), which `GuestsView` renders with `ToneChip` and its own labels. Those
  ten still owe a glossary entry each (§59) — tracked on VRG-734, and it needs the view moved onto
  the `labelledChip` machinery, which is why it is not done here.
*/
